import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { ConversationId, Message, TimelineResponse } from '@cyphex/types';
import { CHAT_KEYS } from '../constants';

/**
 * The infinite-query shape for one conversation's timeline. Page 0 is the
 * NEWEST window; React Query appends older pages as the user scrolls up.
 */
export type TimelinePages = InfiniteData<TimelineResponse, string | undefined>;

/** Oldest → newest, the order the virtualised timeline renders. */
export function flattenTimeline(data: TimelinePages | undefined): Message[] {
  if (!data) return [];
  return [...data.pages].reverse().flatMap((page) => page.messages);
}

/**
 * In-place cache write. No-ops when the timeline is not cached yet.
 *
 * PREFIX match, not an exact key: one conversation can have several cached
 * timelines at once — the live tail `['chat','timeline',id]`, a permalink
 * window `[…,'around',messageId]`, and each open thread `[…,'thread',rootId]`.
 * They hold the same messages, so an edit or a reaction has to land in all of
 * them. Writing only the exact key is how a permalink view ends up showing a
 * message that was deleted three seconds ago.
 */
export function writeTimeline(
  client: QueryClient,
  conversationId: ConversationId,
  update: (data: TimelinePages) => TimelinePages,
): void {
  client.setQueriesData<TimelinePages>({ queryKey: CHAT_KEYS.timeline(conversationId) }, (data) =>
    data ? update(data) : data,
  );
}

export function mapTimelineMessages(
  data: TimelinePages,
  fn: (message: Message) => Message,
): TimelinePages {
  return {
    ...data,
    pages: data.pages.map((page) => ({ ...page, messages: page.messages.map(fn) })),
  };
}
